import { MutableRefObject, useMemo, useRef, useState } from 'react';
import { ChartApi } from '@/components';
import { HeatmapStatLineType, MIN_STEP_FALLBACK_MS } from '@/core';
import { HeatMapChartData } from '../heat-map-model';
import { calculateStatValue, getStatLineColor, getStatLineLabel } from './stat-lines';

export interface HoveredInfo {
  timestamp: number;
  bucket: { lower: number; upper: number } | null;
  value: number | null;
  statLineValues: Array<{ label: string; value: number; color: string }>;
}

export interface CellHighlight {
  top: number;
  height: number;
  left: number;
  width: number;
}

export interface HoverPosition {
  timeMs: number;
  x: number;
  y: number;
}

export interface HeatmapTooltipResult {
  hoverPosition: HoverPosition | null;
  setHoverPosition: (v: HoverPosition | null) => void;
  rafRef: MutableRefObject<number | null>;
  hoveredInfo: HoveredInfo | null;
  cellHighlight: CellHighlight | null;
}

/**
 * Hover state, bucket/value resolution at the cursor, and cell highlight geometry for the explorer heatmap.
 */
export function useHeatmapTooltip(
  chartApiRef: MutableRefObject<ChartApi | undefined>,
  gridRect: { x: number; y: number; width: number; height: number } | undefined,
  data: HeatMapChartData,
  trimmedBounds: { min: number; max: number },
  yScaleFactor: number,
  statLines: HeatmapStatLineType[] | undefined
): HeatmapTooltipResult {
  const [hoverPosition, setHoverPosition] = useState<HoverPosition | null>(null);
  const rafRef = useRef<number | null>(null);

  const hoveredInfo = useMemo<HoveredInfo | null>(() => {
    if (!hoverPosition) return null;
    if (!gridRect || !chartApiRef.current) return null;

    const yDataValue = chartApiRef.current.convertFromPixel({ gridIndex: 0 }, [0, hoverPosition.y])?.[1];
    if (yDataValue === undefined || yDataValue === null) return null;
    const realY = yDataValue / yScaleFactor;

    const stepMs =
      typeof data.timeRange.stepMs === 'number' && data.timeRange.stepMs > 0
        ? data.timeRange.stepMs
        : MIN_STEP_FALLBACK_MS;

    let timeSlice = null;
    let bestTimestamp = 0;
    for (const ts of data.timeSlices) {
      if (ts.timestamp <= hoverPosition.timeMs && ts.timestamp + stepMs > hoverPosition.timeMs) {
        timeSlice = ts;
        bestTimestamp = ts.timestamp;
        break;
      }
      if (ts.timestamp > hoverPosition.timeMs) break;
      timeSlice = ts;
      bestTimestamp = ts.timestamp;
    }

    if (!timeSlice) return null;

    let bucket: { lower: number; upper: number } | null = null;
    let value: number | null = null;

    if (timeSlice) {
      for (const dp of timeSlice.dataPoints) {
        const startB = data.buckets[dp.bucketIndex];
        const endB = data.buckets[dp.bucketIndex + dp.bucketSpan - 1];
        if (!startB || !endB) continue;
        const lower = Number.isFinite(startB.lower) ? startB.lower : trimmedBounds.min;
        const upper = Number.isFinite(endB.upper) ? endB.upper : trimmedBounds.max;
        if (realY >= lower && realY < upper) {
          bucket = { lower, upper };
          value = dp.value;
          break;
        }
      }
      if (!bucket && data.buckets.length > 0) {
        for (let i = 0; i < data.buckets.length; i++) {
          const b = data.buckets[i]!;
          const lower = Number.isFinite(b.lower) ? b.lower : trimmedBounds.min;
          const upper = Number.isFinite(b.upper) ? b.upper : trimmedBounds.max;
          if (realY >= lower && realY < upper) {
            bucket = { lower, upper };
            value = 0;
            break;
          }
        }
      }
    }

    const statLineValues: Array<{ label: string; value: number; color: string }> = [];
    if (timeSlice && statLines?.length) {
      for (const lineType of statLines) {
        const sv = calculateStatValue(lineType, timeSlice.dataPoints, data.buckets);
        if (sv !== null) {
          statLineValues.push({
            label: getStatLineLabel(lineType),
            value: sv,
            color: getStatLineColor(lineType),
          });
        }
      }
    }

    return {
      timestamp: bestTimestamp,
      bucket,
      value,
      statLineValues,
    };
  }, [hoverPosition, gridRect, yScaleFactor, data, trimmedBounds, statLines, chartApiRef]);

  const cellHighlight = useMemo<CellHighlight | null>(() => {
    if (!hoveredInfo?.bucket || !gridRect || !chartApiRef.current || !hoverPosition) return null;
    const api = chartApiRef.current;
    const stepMs =
      typeof data.timeRange.stepMs === 'number' && data.timeRange.stepMs > 0
        ? data.timeRange.stepMs
        : MIN_STEP_FALLBACK_MS;
    const topPx = api.convertToPixel({ gridIndex: 0 }, [0, hoveredInfo.bucket.upper * yScaleFactor]);
    const botPx = api.convertToPixel({ gridIndex: 0 }, [0, hoveredInfo.bucket.lower * yScaleFactor]);
    const leftPx = api.convertToPixel({ gridIndex: 0 }, [hoveredInfo.timestamp, 0]);
    const rightPx = api.convertToPixel({ gridIndex: 0 }, [hoveredInfo.timestamp + stepMs, 0]);
    if (!topPx || !botPx || !leftPx || !rightPx) return null;

    const gridTop = gridRect.y;
    const gridBottom = gridRect.y + gridRect.height;
    const clampedTop = Math.max(gridTop, topPx[1]!);
    const clampedBot = Math.min(gridBottom, botPx[1]!);
    if (clampedBot <= clampedTop) return null;

    return {
      top: clampedTop,
      height: clampedBot - clampedTop,
      left: leftPx[0]!,
      width: rightPx[0]! - leftPx[0]!,
    };
  }, [hoveredInfo, gridRect, yScaleFactor, data.timeRange.stepMs, hoverPosition, chartApiRef]);

  return {
    hoverPosition,
    setHoverPosition,
    rafRef,
    hoveredInfo,
    cellHighlight,
  };
}
